
import { HazardType, SafetyStatus, Contact, Achievement } from './types';

export const APP_NAME = "Protectly";

// ISO 3166-1 alpha-2 mapped to numbers
export const EMERGENCY_NUMBERS: Record<string, { police: string, ambulance: string, fire: string, general: string }> = {
    'US': { police: '911', ambulance: '911', fire: '911', general: '911' },
    'GB': { police: '999', ambulance: '999', fire: '999', general: '112' },
    'EU': { police: '112', ambulance: '112', fire: '112', general: '112' }, // Fallback for Europe
    'AE': { police: '999', ambulance: '998', fire: '997', general: '999' },
    'SA': { police: '999', ambulance: '997', fire: '998', general: '112' },
    'EG': { police: '122', ambulance: '123', fire: '180', general: '122' },
    'MA': { police: '190', ambulance: '150', fire: '150', general: '190' }, // Morocco
    'JP': { police: '110', ambulance: '119', fire: '119', general: '119' },
    'CN': { police: '110', ambulance: '120', fire: '119', general: '110' },
    'IN': { police: '100', ambulance: '102', fire: '101', general: '112' },
    'DEFAULT': { police: '911', ambulance: '911', fire: '911', general: '112' }
};

export const DISASTER_PROTOCOLS = {
  [HazardType.FIRE]: {
    title: "WILDFIRE PROTOCOL",
    steps: [
      "Evacuate immediately if ordered.",
      "Wear N95 mask or wet cloth.",
      "Close all windows and vents.",
      "Turn on lights for visibility.",
      "Drive slowly, headlights on."
    ],
    color: "#FF003C"
  },
  [HazardType.EARTHQUAKE]: {
    title: "SEISMIC PROTOCOL",
    steps: [
      "DROP, COVER, and HOLD ON.",
      "Stay away from glass/windows.",
      "If outdoors, move to open area.",
      "Do NOT use elevators.",
      "Expect aftershocks."
    ],
    color: "#D946EF"
  },
  [HazardType.FLOOD]: {
    title: "FLOOD PROTOCOL",
    steps: [
      "Move to higher ground immediately.",
      "Do NOT walk/drive through water.",
      "6 inches of water can knock you down.",
      "Disconnect electrical appliances.",
      "Avoid contact with floodwater."
    ],
    color: "#00F0FF"
  },
  [HazardType.CIVIL]: {
    title: "CIVIL UNREST PROTOCOL",
    steps: [
      "Avoid large crowds/demonstrations.",
      "Stay indoors, lock all doors.",
      "Move away from windows.",
      "Monitor local news feeds.",
      "Keep emergency bag ready."
    ],
    color: "#FFD600"
  },
  [HazardType.TORNADO]: {
    title: "TORNADO PROTOCOL",
    steps: [
      "Go to basement or interior room.",
      "Cover head and neck with arms.",
      "Stay away from windows.",
      "If outside, lie flat in ditch.",
      "Watch for flying debris."
    ],
    color: "#8B5CF6"
  },
  [HazardType.MEDICAL]: {
    title: "BIOHAZARD PROTOCOL",
    steps: [
      "Isolate immediately.",
      "Wear protective gear (Mask/Gloves).",
      "Do not touch face/eyes.",
      "Sanitize hands frequently.",
      "Await medical transport."
    ],
    color: "#00FF94"
  }
};

export const TRANSLATIONS = {
  en: {
    dashboard: "DECK",
    map: "MAP",
    squad: "SQUAD",
    comms: "COMMS",
    tools: "TACTICAL TOOLS",
    report: "REPORT",
    angel: "ANGEL",
    intel: "INTEL",
    profile: "PROFILE",
    online: "ONLINE",
    scanning: "SCANNING LOCATION...",
    atmosphere: "ATMOSPHERE",
    grid: "POWER GRID",
    stable: "STABLE",
    efficiency: "Efficiency",
    liveIntel: "LIVE INTEL",
    decrypting: "DECRYPTING FEED...",
    greeting: "WELCOME BACK",
    noThreats: "NO IMMEDIATE THREATS",
    humidity: "HUMIDITY",
    wind: "WIND",
    manageKey: "API KEY",
    emergency: "EMERGENCY",
    nearby: "NEARBY",
    region: "REGION",
    nation: "NATION",
    refresh: "REFRESH",
    autoUpdate: "AUTO-UPDATE",
    callPolice: "POLICE",
    callMedic: "MEDIC",
    callFire: "FIRE",
    iAmSafe: "I AM SAFE",
    criticalAlert: "CRITICAL THREAT DETECTED"
  },
  ar: {
    dashboard: "الرئيسية",
    map: "الخريطة",
    squad: "الفريق",
    comms: "الاتصال",
    tools: "أدوات تكتيكية",
    report: "بلاغ",
    angel: "الحارس",
    intel: "الذكاء",
    profile: "الملف",
    online: "متصل",
    scanning: "جاري تحديد الموقع...",
    atmosphere: "الجو",
    grid: "شبكة الطاقة",
    stable: "مستقرة",
    efficiency: "الكفاءة",
    liveIntel: "استخبارات",
    decrypting: "جاري فك التشفير...",
    greeting: "أهلاً بك",
    noThreats: "لا توجد تهديدات",
    humidity: "الرطوبة",
    wind: "الرياح",
    manageKey: "المفتاح",
    emergency: "طوارئ",
    nearby: "بالقرب",
    region: "المدينة",
    nation: "الدولة",
    refresh: "تحديث",
    autoUpdate: "تحديث تلقائي",
    callPolice: "الشرطة",
    callMedic: "إسعاف",
    callFire: "إطفاء",
    iAmSafe: "أنا بخير",
    criticalAlert: "تم رصد خطر حقيقي"
  }
};

export const SQUAD_TEMPLATE: Contact[] = [
  {
    id: 'c1',
    name: 'David (Brother)',
    role: 'FAMILY',
    status: SafetyStatus.WARNING,
    battery: 15,
    locationName: 'Downtown Sector 4',
    coordinates: { lat: 0, lng: 0 },
    lastSeen: '5m ago',
    avatarUrl: 'https://ui-avatars.com/api/?name=David&background=FFD600&color=000',
    phoneNumber: '+1234567890',
    isTrackable: true
  },
  {
    id: 'c2',
    name: 'Emma (Medic)',
    role: 'MEDIC',
    status: SafetyStatus.SAFE,
    battery: 92,
    locationName: 'Medical Outpost',
    coordinates: { lat: 0, lng: 0 },
    lastSeen: '1h ago',
    avatarUrl: 'https://ui-avatars.com/api/?name=Emma&background=00FF94&color=000',
    phoneNumber: '+1987654321',
    isTrackable: true
  },
  {
    id: 'c3',
    name: 'Unit 7 Leader',
    role: 'TACTICAL',
    status: SafetyStatus.DANGER,
    battery: 45,
    locationName: 'Perimeter Zone',
    coordinates: { lat: 0, lng: 0 },
    lastSeen: 'Live',
    avatarUrl: 'https://ui-avatars.com/api/?name=Unit+7&background=FF003C&color=FFF',
    phoneNumber: '+1122334455',
    isTrackable: true
  },
  {
    id: 'c4',
    name: 'Sarah',
    role: 'CIVILIAN',
    status: SafetyStatus.SOS,
    battery: 8,
    locationName: 'Unknown Sector',
    coordinates: { lat: 0, lng: 0 },
    lastSeen: 'Just now',
    avatarUrl: 'https://ui-avatars.com/api/?name=Sarah&background=FF003C&color=FFF',
    phoneNumber: '+1555666777',
    isTrackable: true
  }
];

export const HAZARD_TEMPLATE = [
  {
    id: 'a1',
    type: HazardType.FIRE,
    severity: 'critical',
    location: 'North Ridge Forest',
    timestamp: '10m ago',
    distance: '2.4km',
    coordinates: { lat: 0, lng: 0 },
    isOfficial: true,
    description: 'Rapidly spreading wildfire detected moving South-East. Smoke visibility near zero in sectors 4 and 5. Air quality hazardous.',
    advice: [
      'Evacuate immediately if in Sector 4.',
      'Soak a cloth and cover mouth/nose.',
      'Drive with lights on, close windows.',
      'Do not attempt to outrun fire uphill.'
    ]
  },
  {
    id: 'a2',
    type: HazardType.FLOOD,
    severity: 'medium',
    location: 'River District',
    timestamp: '25m ago',
    distance: '1.8km',
    coordinates: { lat: 0, lng: 0 },
    isOfficial: true,
    description: 'River levels breaching containment wall. Flash flooding expected in low-lying residential zones.',
    advice: [
      'Move to higher ground (Level 2+).',
      'Do not walk or drive through water.',
      'Disconnect electrical appliances.',
      'Avoid contact with floodwater (contamination).'
    ]
  },
  {
    id: 'a3',
    type: HazardType.CIVIL,
    severity: 'low',
    location: 'Central Plaza',
    timestamp: '2m ago',
    distance: '0.5km',
    coordinates: { lat: 0, lng: 0 },
    isOfficial: false,
    verifiedCount: 14,
    description: 'Unverified reports of large crowd gathering and road blockage. Potential for localized disruption.',
    advice: [
      'Avoid Central Plaza intersection.',
      'Keep car doors locked if driving.',
      'Monitor official news feeds.',
      'Stay indoors if residing nearby.'
    ]
  },
  {
    id: 'a4',
    type: HazardType.TORNADO,
    severity: 'high',
    location: 'East Wing',
    timestamp: '1m ago',
    distance: '8.2km',
    coordinates: { lat: 0, lng: 0 },
    isOfficial: true,
    description: 'Doppler radar indicates rotation. Funnel cloud spotted near East Wing industrial park moving East at 45mph.',
    advice: [
      'Seek shelter in basement or interior room.',
      'Stay away from windows.',
      'Cover head with arms or mattress.',
      'If outside, find a low lying ditch.'
    ]
  }
];

export const ACHIEVEMENTS_TEMPLATE: Achievement[] = [
  {
    id: 'ach1',
    title: 'First Responder',
    description: 'Reported a confirmed hazard.',
    icon: '🚨',
    unlocked: true,
    date: '2023-10-15'
  },
  {
    id: 'ach2',
    title: 'Survivalist',
    description: 'Kept battery above 20% for 7 days.',
    icon: '🔋',
    unlocked: true,
    date: '2023-11-02'
  },
  {
    id: 'ach3',
    title: 'Guardian',
    description: 'Verified safety of 5 squad members.',
    icon: '🛡️',
    unlocked: false
  },
  {
    id: 'ach4',
    title: 'Network Node',
    description: 'Connected to the grid for 30 days straight.',
    icon: '🌐',
    unlocked: false
  }
];
